import React, { ForwardedRef, useCallback, useMemo } from 'react';

import { Text, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import { StreamTypes } from 'store/player/actions.types';
import { PlaybackTypes, RBAssetType } from 'store/railsBuilder/reducer.types';
import { startSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';
import { selectLanguage } from 'store/user/selectors';

import { CdvrRecording, ContentType } from 'services/data-manager/types';

import isFunction from 'utils/function';
import getOrdinal from 'utils/getOrdinary.ts';
import useCdvrRecordingItemParentalRating from 'utils/hooks/useCdvrRecordingItemParentalRating';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';
import { selectCdvrRecordingMetadataBaseOnLanguage } from 'utils/recordings';

import { DetailsRequestUrlType } from 'screens/Detail/types';

import BaseTileImage from 'components/BaseTileImage';
import ContentMarkerIcons from 'components/ContentMarkerIcons';
import RibbonTouchable from 'components/CustomButtons/RibbonTouchable';
import {
    ImageContentType,
    ImageSize,
    ImageType,
} from 'components/NagraImage/types';
import { LockedContentCard } from 'components/Ribbon/components/LockedContentCard';
import StaticProgressBar from 'components/Ribbon/components/StaticProgressBar';
import { OnUavAnalyticsProps, StandardUavProps } from 'components/Ribbon/types';

import ProgramLogo from '../../components/ProgramLogo';
import { TileTitle } from '../../components/TileTitle';
import { PLACEHOLDER_ICON_CENTER } from '../../constants';

const {
    CARDS_TITLE_CONTENT_LOCKED,
    ACCESSIBILITY: {
        ACCESSIBILITY_COLLECTION_FIRST_ASSET,
        STRIPE: { ACCESSIBILITY_STRIPE_FIRST_ASSET },
    },
} = LANGUAGE_KEYS;

type Props = {
    item: CdvrRecording;
    index?: number;
    isCollection?: boolean;
    testID?: string;
    totalNumberOfItems?: number;
    onShowPinDialog?(): void;
    onSetParentalControlCheckedCallback?(
        callback: undefined | (() => void),
    ): void;
} & OnUavAnalyticsProps &
    StandardUavProps;

function ContinueWatchingCdvrRecording(
    {
        item,
        index = 0,
        isCollection,
        testID,
        railId,
        templateId,
        totalNumberOfItems,
        onShowPinDialog,
        onSetParentalControlCheckedCallback,
        onRailsSelection,
    }: Props,
    forwardedRef?: ForwardedRef<View | undefined>,
) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigation =
        useNavigation<
            StackNavigationProp<RouteNavigationParams, SCREEN.DETAILS>
        >();

    const currentLanguage = useSelector(selectLanguage);
    const { primary } = useSelector(selectLayoutConfig);

    const profileParentalControlRating = useProfileParentalRating();
    const cdvrRecordingMetadata = useMemo(
        () =>
            selectCdvrRecordingMetadataBaseOnLanguage(
                item.metadata,
                currentLanguage,
            ),
        [item.metadata, currentLanguage],
    );

    const parentalRating = useCdvrRecordingItemParentalRating(
        item as CdvrRecording,
    );

    const isContentLocked = needsToLockContent(
        profileParentalControlRating,
        parentalRating,
    );

    const isSeries = !!item.seriesId;
    const id = item.contentId ?? item.id ?? '';
    const title = cdvrRecordingMetadata?.title;
    const season = cdvrRecordingMetadata?.SeasonNumber;
    const episode = cdvrRecordingMetadata?.episodeNumber;

    const accessibilityLabel = useMemo(() => {
        if (isContentLocked) {
            return t(CARDS_TITLE_CONTENT_LOCKED);
        }

        let label = '';

        if (season) {
            label += `${t('HOVER_SEASON')}${season} `;
        }

        if (episode) {
            label += `${t('HOVER_EPISODE')}${episode} `;
        }

        label += `${title} `;

        const firstAssetKey = isCollection
            ? ACCESSIBILITY_COLLECTION_FIRST_ASSET
            : ACCESSIBILITY_STRIPE_FIRST_ASSET;
        const orderAssetKey = 'ACCESSIBILITY_STRIPE_%NUMBER%_ASSET';

        if (index === 0) {
            label += t(firstAssetKey, {
                number: totalNumberOfItems,
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            });
        } else {
            label += t(orderAssetKey, {
                number: getOrdinal(index + 1),
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            });
        }

        return label;
    }, [
        isContentLocked,
        season,
        episode,
        title,
        index,
        totalNumberOfItems,
        t,
        isCollection,
    ]);

    const onPressCatchup = useCallback(() => {
        onRailsSelection?.({
            id,
            index,
            playbackType: StreamTypes.catchup,
            deeplinkProviderId: '',
            providerResourceId: '',
        });

        dispatch(
            startSentryTransaction(SENTRY_TRANSACTIONS.DetailPageRendering),
        );

        if (isSeries) {
            navigation.navigate(SCREEN.DETAILS, {
                railId,
                templateId,
                playbackType: PlaybackTypes.npvr_event,
                type: RBAssetType.PROGRAMME,
                filterType: DetailsRequestUrlType.PROGRAM,
                id,
                channelId: item.channelId,
                seriesId: item.seriesId,
                season: cdvrRecordingMetadata.SeasonNumber,
                episode: cdvrRecordingMetadata.episodeNumber,
            });
        } else {
            navigation.navigate(SCREEN.DETAILS, {
                railId,
                templateId,
                playbackType: PlaybackTypes.npvr_event,
                type: RBAssetType.PROGRAMME,
                filterType: DetailsRequestUrlType.PROGRAM,
                id,
                channelId: item.channelId,
            });
        }
    }, [
        onRailsSelection,
        id,
        index,
        dispatch,
        isSeries,
        navigation,
        railId,
        templateId,
        item.channelId,
        item.seriesId,
        cdvrRecordingMetadata.SeasonNumber,
        cdvrRecordingMetadata.episodeNumber,
    ]);

    const handlePress = useCallback(() => {
        if (isContentLocked && isFunction(onShowPinDialog)) {
            if (isFunction(onSetParentalControlCheckedCallback)) {
                onSetParentalControlCheckedCallback(onPressCatchup);
            }

            onShowPinDialog();
        } else {
            onPressCatchup();
        }
    }, [
        isContentLocked,
        onShowPinDialog,
        onPressCatchup,
        onSetParentalControlCheckedCallback,
    ]);

    const RibbonContentComponent = useMemo(
        () =>
            isContentLocked ? (
                <LockedContentCard channelId={item.channelId} />
            ) : (
                <>
                    <BaseTileImage
                        style={gStyles.flex}
                        placeHolder={PLACEHOLDER_ICON_CENTER}
                        resizeMode="cover"
                        contentId={id}
                        contentType={
                            isSeries
                                ? ImageContentType.EPISODE
                                : item.contentType
                        }
                        imageType={
                            isSeries ? ImageType.EPISODE : ImageType.BANNER
                        }
                        width={ImageSize.LANDSCAPE.width}
                    />
                    <ProgramLogo channelId={item.channelId} />
                    <ContentMarkerIcons
                        parentalRating={parentalRating}
                        isLimitedToOne
                        isOnLiveCard
                        isOnCard
                    />
                    <StaticProgressBar
                        position={item.position}
                        duration={item.captureDuration}
                        backgroundColor={primary}
                        contentType={ContentType.ContinueWatching}
                    />
                </>
            ),
        [
            id,
            isContentLocked,
            isSeries,
            item.captureDuration,
            item.channelId,
            item.contentType,
            item.position,
            parentalRating,
            primary,
        ],
    );

    const RibbonInfoComponent = useMemo(
        () => (
            <TileTitle
                title={
                    isContentLocked ? (
                        t(CARDS_TITLE_CONTENT_LOCKED)
                    ) : (
                        <>
                            {!!season && (
                                <Text>{`${t('HOVER_SEASON')}${season} `}</Text>
                            )}
                            {!!episode && (
                                <Text>
                                    {`${t('HOVER_EPISODE')}${episode} `}
                                </Text>
                            )}
                            {title}
                        </>
                    )
                }
            />
        ),
        [episode, isContentLocked, season, t, title],
    );

    return (
        <RibbonTouchable
            ref={forwardedRef}
            testID={testID}
            metadataType={ContentType.Catchup}
            isCollection={isCollection}
            onPress={handlePress}
            railId={railId}
            accessibilityLabel={accessibilityLabel}
            RibbonContentComponent={RibbonContentComponent}
            RibbonInfoComponent={RibbonInfoComponent}
        />
    );
}

export default React.forwardRef(ContinueWatchingCdvrRecording);
